import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { allVideos, categoryTabs } from '@/mocks/category';
import { allArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState(currentTab);

  useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab]);

  const handleTabChange = (tabId: string) => {
    setSearchParams(tabId === 'all' ? {} : { tab: tabId });
  };

  const filteredVideos = useMemo(() => {
    if (activeTab === 'articles') return [];
    if (activeTab === 'all') return allVideos;
    return allVideos.filter((v) => v.category === activeTab);
  }, [activeTab]);

  const isArticlesTab = activeTab === 'articles';
  const currentTabName = categoryTabs.find((t) => t.id === activeTab)?.name || activeTab;

  const categorySchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://qiezi.video/category#webpage",
        "url": `https://qiezi.video/category?tab=${activeTab}`,
        "name": `${currentTabName} - 茄子视频高清在线观看`,
        "description": `茄子视频${currentTabName}频道，海量高清内容免费在线观看`,
        "inLanguage": "zh-CN",
        "isPartOf": { "@id": "https://qiezi.video/#website" }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "首页",
            "item": "https://qiezi.video/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": currentTabName,
            "item": `https://qiezi.video/category?tab=${activeTab}`
          }
        ]
      }
    ]
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium whitespace-nowrap">
              {activeTab === 'all' ? '全部分类' : currentTabName}
            </span>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-2">
              {activeTab === 'all' ? '分类浏览' : currentTabName}
            </h1>
            <p className="text-sm text-foreground-500">
              {isArticlesTab
                ? '最新影视资讯、观影指南和平台动态'
                : `发现精彩内容，畅享高清观影体验${activeTab !== 'all' ? ` · 共${filteredVideos.length}部` : ''}`
              }
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="inline-flex bg-background-100 rounded-full px-1 py-1 flex-wrap gap-0.5">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {!isArticlesTab && (
          <section className="w-full px-4 md:px-6 pb-16">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredVideos.map((video) => (
                  <div key={video.id} className="group cursor-pointer">
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
                      <img
                        src={video.imageUrl}
                        alt={video.title}
                        title={`${video.title} - 茄子视频高清在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500">
                          <i className="ri-play-fill text-white text-base"></i>
                        </div>
                      </div>
                      <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                        {video.tags.map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-accent-500 text-white whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="absolute bottom-2 right-2 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-black/60 text-accent-400 whitespace-nowrap">
                        {video.rating}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5 whitespace-nowrap">
                      {video.category}{video.episodes > 1 ? ` · ${video.episodes}集` : ''}
                    </p>
                  </div>
                ))}
              </div>
              {filteredVideos.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 mb-4">
                    <i className="ri-film-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">暂无内容，请选择其他分类</p>
                </div>
              )}
            </div>
          </section>
        )}

        {isArticlesTab && (
          <section className="w-full px-4 md:px-6 pb-16">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col gap-4">
                {allArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group cursor-pointer bg-white rounded-xl p-4 md:p-5 flex flex-col sm:flex-row gap-4 hover:bg-background-50 transition-colors"
                  >
                    <div className="relative w-full sm:w-48 md:w-56 aspect-[16/9] sm:aspect-[4/3] rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-2 py-0.5 text-[11px] font-medium rounded-md bg-secondary-100 text-secondary-700 whitespace-nowrap">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime} 阅读</span>
                      </div>
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1.5">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                        {article.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
    </>
  );
}